import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  THEME_STORAGE_KEY,
  applyTheme,
  isThemePreference,
  resolveTheme,
  type ResolvedTheme,
  type ThemePreference,
} from './theme';

interface ThemeValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

const DARK_QUERY = '(prefers-color-scheme: dark)';

export interface ThemeProviderProps {
  children: ReactNode;
  /**
   * Whether this page is genuinely themeable. The public site is light-only
   * (no `ThemeToggle` is ever mounted there — see `Header`'s doc comment) and
   * the server has already stamped the correct `data-theme` on `<html>`
   * (`+onRenderHtml.tsx`). Without this gate, the effects below still ran on
   * every public page: they read the visitor's OS `prefers-color-scheme` and
   * wrote whatever that resolved to onto `<html>`, silently flipping a
   * dark-mode visitor's light public page back to dark right after hydration
   * — the SSR output was correct and a client effect was undoing it.
   */
  enabled?: boolean;
}

export function ThemeProvider({ children, enabled = true }: ThemeProviderProps) {
  // Server-rendered markup carries no preference — the inline script has
  // already applied the right theme to <html> before React runs. Starting from
  // 'system' keeps the server and client trees identical, so hydration does not
  // mismatch and then correct itself with a visible repaint.
  const [preference, setPreferenceState] = useState<ThemePreference>('system');
  const [prefersDark, setPrefersDark] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const stored = readStoredPreference();
    if (stored) setPreferenceState(stored);

    const media = window.matchMedia(DARK_QUERY);
    setPrefersDark(media.matches);

    const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [enabled]);

  const resolved = resolveTheme(preference, prefersDark);

  useEffect(() => {
    if (!enabled) return;
    applyTheme(resolved, document.documentElement);
  }, [enabled, resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Private or partitioned storage. The choice still applies for this
      // session; only its persistence is lost, which is not worth an error.
    }
  }, []);

  const value = useMemo<ThemeValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error('useTheme was called outside ThemeProvider');
  }
  return value;
}

function readStoredPreference(): ThemePreference | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : null;
  } catch {
    return null;
  }
}
